import React from "react";
import { Dispatch, ReactNode, SetStateAction, createContext as reactCreateContext, useEffect, useState } from "react";

type ContextTuple<TContext> = [TContext | undefined, Dispatch<SetStateAction<TContext | undefined>>];
type ReactContextType<TContext> = React.Context<ContextTuple<TContext>>;

export interface ContextProviderProps {
  children: ReactNode;
}

export const createContext = <TContext,>() =>
  reactCreateContext<ContextTuple<TContext>>([
    undefined,
    () => {
      throw new Error("This context has no provider");
    },
  ]);

export const generateContextProvider = <TContext, TProps extends ContextProviderProps>(
  ReactContext: ReactContextType<TContext>,
  fetchContextData?: () => Promise<TContext | undefined>,
) => {
  const contextProvider = ({ children }: TProps) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [contextState, setContextState] = useState<TContext>();

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      (async () => {
        try {
          if (fetchContextData) {
            const contextData = await fetchContextData();
            setContextState(contextData);
          }
        } catch (error) {
          setContextState(undefined);
        }
      })();
    }, [setContextState]);

    return <ReactContext.Provider value={[contextState, setContextState]}>{children}</ReactContext.Provider>;
  };

  return contextProvider;
};
